/**
 * Approve a device — the port of views/activate.ejs.
 *
 * A focused task page: one centred card, a code the device shows, then a confirmation with the device's own
 * name prefilled and editable (the label is the reader's note about the token, not the device's claim about
 * itself). The three steps and the two writes are the page's: `/api/device/lookup`, then
 * `/api/device/approve` with an approve or deny decision.
 */
import { useState } from 'react';

type Step = 'code' | 'confirm' | 'done';

/** The server forgives case, hyphens and spaces; the field only makes the grouping readable. */
function typedCode(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
}

export function Activate() {
  const initial = new URLSearchParams(window.location.search).get('code') || '';
  const [code, setCode] = useState(initial ? typedCode(initial) : '');
  const [step, setStep] = useState<Step>('code');
  const [error, setError] = useState<string | null>(null);
  const [device, setDevice] = useState('Device');
  const [label, setLabel] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);

  const fail = (msg: string) => setError(msg);

  const finish = (what: 'approve' | 'deny' | 'already', named?: string) => {
    setStep('done');
    setDone(
      what === 'deny'
        ? 'Denied. The device was not given access — it will show an error and can start again.'
        : what === 'already'
          ? 'That code has already been approved. The device will connect on its own; it is in your token list, where you can rename or revoke it at any time.'
          : `Approved${named ? ` as “${named}”` : ''}. The device will connect within a few seconds and then appear in your token list, where you can rename or revoke it at any time.`,
    );
  };

  const lookup = async () => {
    setError(null);
    const current = typedCode(code);
    if (current.replace('-', '').length !== 8) return fail('Enter the 8-character code shown on the device.');
    setBusy(true);
    try {
      const res = await fetch(`/api/device/lookup?code=${encodeURIComponent(current)}`, {
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });
      const body = (await res.json()) as { ok?: boolean; error?: string; approved?: boolean; device?: string };
      if (!body.ok) return fail(body.error || 'That code could not be checked.');
      if (body.approved) return finish('already');
      setDevice(body.device || 'Unnamed device');
      setLabel(body.device || '');
      setStep('confirm');
    } catch {
      fail('Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (decision: 'approve' | 'deny') => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch('/api/device/approve', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ user_code: typedCode(code), decision, ...(decision === 'approve' ? { label } : {}) }),
      });
      const body = (await res.json()) as { ok?: boolean; error?: string; label?: string };
      if (!body.ok) return fail(body.error || `Could not ${decision} that code.`);
      finish(decision, body.label);
    } catch {
      fail('Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main>
      <div className="activate-wrap">
      <div className="activate-card">
        <h1>Approve a device</h1>
        <p className="lede">
          A watch or other device is asking to connect. Type the code it shows on its screen. Approving lets it{' '}
          <strong>read</strong> your quotes — it cannot change anything.
        </p>

        {error ? (
          <div id="activate-err" className="activate-err">
            {error}
          </div>
        ) : null}

        {step === 'code' ? (
          <div id="step-code">
            <label htmlFor="code">Code shown on the device</label>
            <input
              id="code"
              className="activate-code"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={9}
              placeholder="XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(typedCode(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void lookup();
              }}
            />
            <div className="activate-actions">
              <button type="button" className="btn" id="btn-continue" onClick={() => void lookup()} disabled={busy}>
                Continue
              </button>
            </div>
          </div>
        ) : null}

        {step === 'confirm' ? (
          <div id="step-confirm">
            <div className="activate-device">
              <p className="name" id="device-name">
                {device}
              </p>
              <p className="what">
                Read-only access to your portfolio&apos;s quotes. It cannot see your password, and it cannot
                change your lists or transactions.
              </p>
            </div>
            <label htmlFor="device-label">Name it</label>
            <input
              id="device-label"
              maxLength={60}
              autoComplete="off"
              spellCheck={false}
              placeholder="e.g. Chase's Apple Watch"
              value={label}
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setLabel(e.target.value)}
            />
            <p className="activate-note">
              That is what the device calls itself. This name is yours — it is what you will see in your token
              list, and you can change it there later.
            </p>
            <div className="activate-actions">
              <button type="button" className="btn" id="btn-approve" onClick={() => void decide('approve')} disabled={busy}>
                Approve
              </button>
              <button
                type="button"
                className="btn"
                id="btn-deny"
                onClick={() => void decide('deny')}
                disabled={busy}
                style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)' }}
              >
                Deny
              </button>
            </div>
          </div>
        ) : null}

        {step === 'done' ? (
          <div id="step-done">
            <div className="activate-ok" id="done-text">
              {done}
            </div>
          </div>
        ) : null}

        <p className="activate-hint">
          The device appears in <a href="/settings/tokens">Settings → Access tokens</a> once connected, so you
          can revoke it there at any time. Codes expire after five minutes; if yours has, ask the device for a
          new one. This page never sees the device&apos;s credential — it only grants the request.
        </p>
        </div>
      </div>
    </main>
  );
}
